// frontend/src/pages/OrderConfirmationPage.jsx
// UPDATED: Shows correct fee breakdown (platform fee, logistics fee, CA sales tax)
// FIXED: Total Charged reflects actual amount processed by Stripe

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { 
  Check, 
  Package, 
  MapPin, 
  Truck, 
  Calendar,
  Mail,
  Star,
  ShoppingBag,
  Coins,
  MessageSquare,
  Tag
} from 'lucide-react';
import OrderFeedbackModal from '../components/feedback/OrderFeedbackModal';

// Query to get purchase details — includes fee breakdown fields stored at payment time
const GET_PURCHASE = gql`
  query GetPurchase($id: ID!) {
    purchase(id: $id) {
      id
      status
      totalAmount
      savingsAmount
      tokenRedemption
      tokenDiscount
      feeSubtotal
      feePlatform
      feeLogistics
      feeTax
      feeTotal
      createdAt
      products {
        quantity
        priceAtPurchase
        product {
          id
          name
          currentPrice
          basePrice
        }
      }
      logistics {
        type
        scheduledTime
        deliveryAddress {
          address
          city
          state
        }
      }
    }
  }
`;

// Query to get tokens EARNED from this purchase
const GET_PURCHASE_TOKENS = gql`
  query PurchaseTokens($purchaseId: ID!) {
    purchaseTokens(purchaseId: $purchaseId) {
      id
      amount
      description
      createdAt
      metadata {
        quantity
        tierReached
        multiplier
      }
    }
  }
`;


const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  
  const { data, loading, error } = useQuery(GET_PURCHASE, {
    variables: { id: orderId || '' },
    skip: !orderId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    onError: (error) => {
      console.error('Error fetching purchase:', error);
    }
  });

  const { data: tokensData, loading: tokensLoading } = useQuery(GET_PURCHASE_TOKENS, {
    variables: { purchaseId: orderId || '' },
    skip: !orderId,
    fetchPolicy: 'network-only',
    onError: (error) => {
      console.error('Error fetching purchase tokens:', error);
    }
  });

  useEffect(() => {
    if (orderId) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [orderId]);

  const order = data?.purchase || orderData;

  // ─── Fee breakdown ────────────────────────────────────────────────────────
  // Prefer fields stored at payment time (feeSubtotal, feePlatform, etc.)
  // Fall back to calculating from products if order was placed before this fix
  const calculatedSubtotal = useMemo(() => {
    return order?.products?.reduce((sum, item) => {
      const price = item.priceAtPurchase || item.product?.currentPrice || item.product?.basePrice || 0;
      return sum + (price * item.quantity);
    }, 0) || 0;
  }, [order]);

  const feeSubtotal   = order?.feeSubtotal   ?? calculatedSubtotal;
  const feePlatform   = order?.feePlatform   ?? Math.round(feeSubtotal * 0.075 * 100) / 100;
  const feeLogistics  = order?.feeLogistics  ?? Math.round(feeSubtotal * 0.075 * 100) / 100;
  const feeTax        = order?.feeTax        ?? Math.round(feeSubtotal * 0.0725 * 100) / 100;
  const feeTotal      = order?.feeTotal      ?? (feeSubtotal + feePlatform + feeLogistics + feeTax);

  const tokenDiscount = order?.tokenDiscount || 0;
  const tokensRedeemed = order?.tokenRedemption || 0;

  // What was actually charged to the card
  const totalCharged = feeTotal;
  // ─────────────────────────────────────────────────────────────────────────

  const tokensEarned = useMemo(() => {
    if (tokensData?.purchaseTokens?.amount) {
      return tokensData.purchaseTokens.amount;
    }
    if (order?.products) {
      const itemCount = order.products.reduce((sum, item) => sum + item.quantity, 0);
      const baseTokens = itemCount * 10;
      const pickupBonus = order?.logistics?.type === 'PICKUP' ? 2 * itemCount : 0;
      return baseTokens + pickupBonus;
    }
    return 0;
  }, [tokensData, order]);

  const deliveryFee = 0; // Free delivery during launch promo
  
  const estimatedDelivery = () => {
    const days = order?.logistics?.type === 'DELIVERY' ? 3 : 1;
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleStarClick = (rating) => {
    setSelectedRating(rating);
    setShowFeedbackModal(true);
  };

  if (!orderId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">No order ID provided</p>
          <button 
            onClick={() => navigate('/orders')} 
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            View All Orders
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find this order.</p>
          <button 
            onClick={() => navigate('/orders')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            View All Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fall"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][Math.floor(Math.random() * 5)]
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Feedback Modal */}
      <OrderFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        purchaseId={orderId}
        orderTotal={totalCharged}
        initialRating={selectedRating}
      />

      {/* Success Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <Check size={40} className="text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-lg text-gray-600 mb-4">Thank you for your purchase</p>
          <p className="text-sm text-gray-500">
            Order #{orderId?.slice(-8).toUpperCase() || 'XXXXX'}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Email Confirmation Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
          <Mail className="text-blue-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Confirmation Email Sent</h3>
            <p className="text-sm text-blue-700">
              We've sent a confirmation email with your order details and tracking information.
            </p>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">What Happens Next?</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-sm font-semibold text-blue-600">1</span>
              </div>
              <div className="ml-4">
                <h3 className="font-medium">Order Processing</h3>
                <p className="text-sm text-gray-600 mt-1">We're preparing your order and will notify you once it's ready.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-sm font-semibold text-blue-600">2</span>
              </div>
              <div className="ml-4">
                <h3 className="font-medium">
                  {order?.logistics?.type === 'DELIVERY' ? 'Shipment' : 'Pickup Ready'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {order?.logistics?.type === 'DELIVERY'
                    ? "We'll send tracking information once your order ships."
                    : "We'll notify you when your order is ready for pickup."}
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-sm font-semibold text-blue-600">3</span>
              </div>
              <div className="ml-4">
                <h3 className="font-medium">
                  {order?.logistics?.type === 'DELIVERY' ? 'Delivery' : 'Pickup'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">Estimated by {estimatedDelivery()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Order Summary — full fee breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Order Summary</h2>

          {/* Line items */}
          <div className="space-y-3 mb-4">
            {order?.products?.map((item, index) => (
              <div key={item.product?.id || index} className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                    <Package size={20} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium">{item.product?.name || 'Product'}</p>
                    <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                  </div>
                </div>
                <p className="font-medium">
                  ${((item.priceAtPurchase || item.product?.currentPrice || item.product?.basePrice || 0) * item.quantity).toFixed(2)}
                </p>
              </div>
            )) || (
              <div className="text-center py-8 text-gray-500">
                <Package size={40} className="mx-auto mb-2 text-gray-300" />
                <p>Loading order details...</p>
              </div>
            )}
          </div>

          {/* Price Breakdown — matches checkout page exactly */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span>${feeSubtotal.toFixed(2)}</span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                Platform Fee <span className="text-xs text-gray-400">(7.5%)</span>
              </span>
              <span>${feePlatform.toFixed(2)}</span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                Logistics Fee <span className="text-xs text-gray-400">(7.5%)</span>
              </span>
              <span>${feeLogistics.toFixed(2)}</span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                CA Sales Tax <span className="text-xs text-gray-400">(7.25%)</span>
              </span>
              <span>${feeTax.toFixed(2)}</span>
            </div>

            {deliveryFee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Delivery</span>
                <span>${deliveryFee.toFixed(2)}</span>
              </div>
            )}

            {/* Token discount */}
            {tokensRedeemed > 0 && (
              <div className="flex justify-between text-sm bg-green-50 -mx-2 px-2 py-2 rounded">
                <span className="text-green-700 flex items-center">
                  <Coins size={14} className="mr-1" />
                  Token Discount ({tokensRedeemed} tokens)
                </span>
                <span className="text-green-700 font-medium">-${tokenDiscount.toFixed(2)}</span>
              </div>
            )}

            {/* Group savings if applicable */}
            {order?.savingsAmount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span className="flex items-center">
                  <Tag size={14} className="mr-1" />
                  Group Savings
                </span>
                <span>-${order.savingsAmount.toFixed(2)}</span>
              </div>
            )}

            {/* Total — matches what Stripe actually charged */}
            <div className="flex justify-between font-semibold text-lg pt-2 border-t">
              <span>Total Charged</span>
              <span>${totalCharged.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Delivery/Pickup Info */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            {order?.logistics?.type === 'DELIVERY' ? (
              <><Truck size={20} className="mr-2" />Delivery Information</>
            ) : (
              <><MapPin size={20} className="mr-2" />Pickup Information</>
            )}
          </h2>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <Calendar size={16} className="text-gray-500 mr-2" />
              <span className="text-sm font-medium">
                Estimated {order?.logistics?.type === 'DELIVERY' ? 'Delivery' : 'Pickup'}: {estimatedDelivery()}
              </span>
            </div>
            {order?.logistics?.scheduledTime && (
              <div className="flex items-center mt-2">
                <Calendar size={16} className="text-gray-500 mr-2" />
                <span className="text-sm text-gray-600">
                  Scheduled: {new Date(order.logistics.scheduledTime).toLocaleString()}
                </span>
              </div>
            )}
            {order?.logistics?.type === 'DELIVERY' && order?.logistics?.deliveryAddress && (
              <div className="text-sm text-gray-600 mt-2">
                <p className="font-medium mb-1">Delivery Address:</p>
                {order.logistics.deliveryAddress.address && (
                  <p>{order.logistics.deliveryAddress.address}</p>
                )}
                <p>
                  {[order.logistics.deliveryAddress.city, order.logistics.deliveryAddress.state]
                    .filter(Boolean).join(', ')}
                </p>
              </div>
            )}
            {(order?.logistics?.type === 'PICKUP' || !order?.logistics?.type) && (
              <div className="text-sm text-gray-600 mt-2">
                <p className="font-medium mb-1">Pickup Location:</p>
                <p>Sacramento Area</p>
                <p className="text-gray-500">Exact location will be confirmed via email/SMS</p>
                <p className="mt-2 text-blue-600">We'll notify you when your order is ready!</p>
              </div>
            )}
          </div>
        </div>

        {/* Token Summary */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <Coins size={20} className="mr-2 text-yellow-600" />
            Token Summary
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Earned</p>
              <p className="text-2xl font-bold text-green-600">
                {tokensLoading ? '...' : `+${tokensEarned}`}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                tokens from this purchase
                {order?.logistics?.type === 'PICKUP' && (
                  <span className="text-green-600"> (incl. pickup bonus!)</span>
                )}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-orange-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Redeemed</p>
              <p className="text-2xl font-bold text-orange-600">
                {tokensRedeemed > 0 ? `-${tokensRedeemed}` : '0'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {tokensRedeemed > 0 ? `saved $${tokenDiscount.toFixed(2)}` : 'no tokens used'}
              </p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-yellow-200 flex justify-between items-center">
            <span className="text-sm text-gray-700">Net Token Change:</span>
            <span className={`font-bold text-lg ${
              tokensEarned - tokensRedeemed >= 0 ? 'text-green-600' : 'text-orange-600'
            }`}>
              {tokensEarned - tokensRedeemed >= 0 ? '+' : ''}{tokensEarned - tokensRedeemed} tokens
            </span>
          </div>
          {tokensData?.purchaseTokens?.metadata?.tierReached && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              Your tier: {tokensData.purchaseTokens.metadata.tierReached}
            </p>
          )}
        </div>

        {/* Feedback Prompt */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
              <MessageSquare size={24} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Quick Feedback?</h3>
              <p className="text-sm text-gray-600 mb-3">
                How was your UniLedge experience? Rate us and earn <span className="font-bold text-purple-600">5-15 tokens</span>!
              </p>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleStarClick(star)}
                    className="p-2 hover:bg-purple-100 rounded-lg transition-colors group"
                  >
                    <Star 
                      size={28} 
                      className="text-purple-300 group-hover:text-yellow-400 group-hover:fill-yellow-400 transition-colors"
                    />
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Your feedback directly impacts our Platform Trust Score!
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/orders')}
            className="bg-white border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center"
          >
            <Package size={20} className="mr-2" />
            View All Orders
          </button>
          <button
            onClick={() => navigate('/home')}
            className="bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <ShoppingBag size={20} className="mr-2" />
            Continue Shopping
          </button>
        </div>

        {/* Help */}
        <div className="text-center py-6">
          <p className="text-sm text-gray-600 mb-2">Need help with your order?</p>
          <button 
            onClick={() => navigate('/support')}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            Contact Customer Support
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-fall { animation: fall linear; }
      `}</style>
    </div>
  );
};

export default OrderConfirmationPage;